import React, { useContext } from 'react';
import { Grid } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import Edit from '@material-ui/icons/Edit';
import { Button } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import QtySelector from 'components/QtySelector/qtySelector';
import { SelectedTire, WizardContext } from 'compositions/Wizard/WizardContext';
import { Part, UFOPart } from 'types/UFOTypes';
import { MAXIMUM_MOUNTED_WHEELS, MAXIMUM_TIRES, TIRE_DEFAULT_QTY } from 'utils/constantValues';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { BrandImage } from '../BrandImage';
import { Price } from './Price';

interface PartInformationProps {
  qty: number;
  setQty: (q: number) => void;
  part: UFOPart;
  printMode?: boolean;
  isEditing: boolean;
  status: string;
  orderPrice?: Part;
  commonPricesParams: CommonPricesParamsType;
  selected?: boolean;
  setSelected?: () => void;
  removeTire?: () => void;
  selectedTires?: SelectedTire[];
  mobileView?: boolean;
  tiresCount?: number;
  stockingOrder: boolean;
  smallSize?: boolean;
  withEditButton?: boolean;
  setMountingOptionsModalOpen: (part: UFOPart) => void;
}

export default function PartInformation(props: PartInformationProps) {
  const {
    selected,
    qty,
    setQty,
    part,
    printMode = false,
    status,
    isEditing,
    orderPrice,
    commonPricesParams,
    setSelected = () => {},
    removeTire = () => {},
    selectedTires,
    mobileView,
    stockingOrder,
    smallSize,
    withEditButton,
    setMountingOptionsModalOpen,
    tiresCount: tiresCountProp,
  } = props;
  const { t } = useTranslation();
  const wizardMode = status === 'wizard';
  const shoppingCartMode = wizardMode && isEditing;

  const { removePartFromGroup } = useContext(WizardContext);

  const remove = () => {
    if (removePartFromGroup) removePartFromGroup(part.mspn);
    removeTire();
  };

  if (!part) return null;

  const {
    image_url: imageUrl,
    size,
    mspn,
    dealer_part_id: dealerPartId,
    manufacturer = '',
    brand_name: brandName = '',
    tire_title: tireTitle = '',
    id,
  } = part;

  type CustomGridSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

  const spacing: CustomGridSize = shoppingCartMode || mobileView ? 12 : 6;

  const priceSpacing: CustomGridSize = shoppingCartMode || mobileView ? 6 : 3;

  const tiresCount =
    tiresCountProp || selectedTires?.reduce((count, tire) => (!tire.removed ? count + (tire.qty || 0) : count), 0) || 0;
  const maximumTires = stockingOrder ? MAXIMUM_MOUNTED_WHEELS : MAXIMUM_TIRES;
  const fixedDefaultValue = maximumTires - tiresCount > TIRE_DEFAULT_QTY ? TIRE_DEFAULT_QTY : maximumTires - tiresCount;

  const SelectQty = (
    <div style={{ transform: mobileView || shoppingCartMode || smallSize ? 'scale(0.8)' : undefined, transformOrigin: shoppingCartMode ? 'center left' : undefined }}>
      {!stockingOrder && (
        <QtySelector
          value={qty}
          onAdd={setSelected}
          onRemove={setSelected}
          maxValue={maximumTires}
          defaultValue={fixedDefaultValue}
          onChange={(v) => setQty(v)}
          selected={!!selected}
          disabled={!isEditing}
          tiresCount={tiresCount}
          shoppingCartMode={shoppingCartMode}
        />
      )}
      {withEditButton && setMountingOptionsModalOpen && stockingOrder && isEditing && (
        <div style={{ width: 160 }}>
          <Button
            onClick={() => setMountingOptionsModalOpen(part)}
            style={{
              height: 32,
              width: 160,
              borderRadius: 20,
              marginTop: 16,
              whiteSpace: 'nowrap',
            }}
            endIcon={<Edit />}
            variant="contained"
            size="small"
          >
            {t('Edit options')}
          </Button>
          <Button
            onClick={remove}
            style={{
              height: 32,
              width: 160,
              borderRadius: 20,
              marginTop: 8,
              whiteSpace: 'nowrap',
            }}
            endIcon={<Delete />}
            variant="contained"
            color="secondary"
            size="small"
          >
            {t('Remove Tire')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Grid
        container
        spacing={2}
        style={{
          paddingBottom: 16,
        }}
      >
        <Grid
          item
          xs={spacing}
          spacing={0}
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          {imageUrl && imageUrl.length > 0 && !printMode && (
            <>
              <div
                style={{
                  transform: mobileView || shoppingCartMode || smallSize ? 'scale(0.8)' : undefined,
                  transformOrigin: 'top left 0',
                  width: mobileView || shoppingCartMode ? 85 : undefined,
                  marginRight: 16,
                }}
              >
                <BrandImage imageName={imageUrl} />
              </div>
            </>
          )}
          <div style={{ display: 'grid' }}>
            <span style={{ fontSize: smallSize ? 12 : 14, fontWeight: 'bold' }}>
              {`${brandName || manufacturer} - ${tireTitle} `}
              <br />
            </span>
            <span style={{ fontSize: smallSize ? 10 : 12 }} id={shoppingCartMode ? 'cartTireNumber' : 'tireNumber'}>
              {size}
              <br />
              <b>{t('Part #: ')}</b>
              {mspn}
              {dealerPartId && <>{`|${dealerPartId}`}</>}
            </span>
            <br />
          </div>
        </Grid>
        <Grid item xs={shoppingCartMode || mobileView ? 6 : 3}>
          {SelectQty}
        </Grid>
        <Grid
          item
          xs={priceSpacing}
          style={
            shoppingCartMode || smallSize
              ? {
                  textAlign: 'right',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }
              : { textAlign: 'right' }
          }
          container={shoppingCartMode}
        >
          <div
            style={
              shoppingCartMode
                ? { textAlign: 'right', width: 'fit-content' }
                : { transform: smallSize ? 'scale(0.8)' : undefined, transformOrigin: 'center right' }
            }
          >
            <Price
              printMode={printMode}
              status={status}
              qty={qty}
              mspn={getMspn(part) as string}
              id={id}
              prices={orderPrice}
              commonPricesParams={commonPricesParams}
              is_pql={false}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
}
